/**
 * MeasureOverviewPage — the Measure-pillar landing dashboard.
 *
 * REBUILT 2026-08-21 (M5). The previous version was written against a payload
 * that does not exist: it expected {totalSent7d, deliveredRate, openRate,
 * activeFunnels/Campaigns/Triggers, channelMix, kindMix, anomalies} while
 * GET /api/admin/communications/overview returns {channels, health, volume,
 * topTemplates, recentActivity} — zero overlapping keys. Every tile therefore
 * fell through to its `?? 0` default and the page rendered an all-zero green
 * "All clear" dashboard on live data. It also carried a hardcoded "Last Send /
 * Live / SSE stream" tile that was pure decoration (no SSE stream exists here).
 *
 * This version renders ONLY what the endpoint actually ships, and labels the
 * scope of every number:
 *   - health + email volume are scoped to the selected range (server echoes it)
 *   - topTemplates sends/open-rate are LIFETIME counters (server flags this)
 *   - recentActivity is the latest 10 sends overall, not range-scoped
 *   - push/SMS are not instrumented; the server marks them `reported: false`
 *     so a 0 is never presented as "nothing was sent".
 *
 * Anomalies are DERIVED from `health` (the endpoint has no anomaly feed), and
 * each one deep-links through react-router — never a raw <a href> that would
 * hard-reload the SPA.
 */

import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Skeleton,
  Alert,
  Button,
  Chip,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
} from "@mui/material";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
} from "recharts";
import PillarPageShell from "../shared/PillarPageShell";
import { MetricCard } from "../../help";
import TimeRangeFilter from "../../common/TimeRangeFilter";
import useTimeRangeParam from "../../common/useTimeRangeParam";
import { describeRange, resolveRangeYmd, toApiToken } from "../../common/timeRange";
import apiService from "../../../services/api";

// ── Server contract (backend/routes/admin/adminCommunicationsOverview.js) ────

interface ChannelEntry {
  sent?: number | null;
  active?: number | null;
  status?: string | null;
  /** false ⇒ nothing feeds this channel yet; a 0 means "unknown", not "none". */
  reported?: boolean;
}

interface OverviewData {
  range?: string;
  range_days?: number;
  channels?: {
    email?: ChannelEntry;
    sms?: ChannelEntry;
    push?: ChannelEntry;
    triggers?: ChannelEntry;
  };
  health?: {
    delivery_rate: number | null;
    open_rate: number | null;
    click_rate: number | null;
    bounce_rate: number | null;
  };
  volume?: {
    labels?: Array<string | null>;
    email?: number[];
    sms?: number[];
    push?: number[];
  };
  topTemplatesScope?: string;
  topTemplates?: Array<{
    name: string | null;
    category: string | null;
    sends: number | null;
    open_rate: number | null;
  }>;
  recentActivity?: Array<{
    subject: string | null;
    status: string | null;
    recipient: string | null;
    time: string | null;
  }>;
}

// ── Formatters (null-safe: a missing number is '—', never a fabricated 0) ────
const fmtInt = (n: number | null | undefined): string =>
  n === null || n === undefined || Number.isNaN(Number(n))
    ? "—"
    : Number(n).toLocaleString();

const fmtPct = (n: number | null | undefined, digits = 1): string =>
  n === null || n === undefined || Number.isNaN(Number(n))
    ? "—"
    : `${Number(n).toFixed(digits)}%`;

/** Guarded date label — an unparseable value renders '—', never "Invalid Date". */
const fmtDay = (raw: string | null | undefined): string => {
  if (!raw) return "—";
  const d = new Date(`${String(raw).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const fmtWhen = (raw: string | null | undefined): string => {
  if (!raw) return "—";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// ── Anomaly derivation ───────────────────────────────────────────────────────
// The endpoint ships no anomaly feed, so the old "Anomalies / All clear" tile
// was reading an always-undefined array. These are computed from `health` and
// each names the threshold it tripped.
type Severity = "info" | "warning" | "error";

/** Allowlisted in-app destinations — never navigate to a value from the API. */
const DEEP_LINKS = {
  deliverability: "/dashboard/communications/measure/deliverability",
  templates: "/dashboard/communications/measure/templates",
  triggers: "/dashboard/communications/measure/triggers",
} as const;
type DeepLinkKey = keyof typeof DEEP_LINKS;

interface Anomaly {
  id: string;
  severity: Severity;
  message: string;
  to: DeepLinkKey;
}

function deriveAnomalies(data: OverviewData | null, sent: number | null): Anomaly[] {
  const h = data?.health;
  if (!h) return [];
  const out: Anomaly[] = [];
  if (h.bounce_rate != null && h.bounce_rate > 2) {
    out.push({
      id: "bounce",
      severity: h.bounce_rate > 5 ? "error" : "warning",
      message: `Bounce rate is ${fmtPct(h.bounce_rate, 2)} — sustained bounces above 2% put sender reputation at risk.`,
      to: "deliverability",
    });
  }
  if (h.delivery_rate != null && h.delivery_rate < 95) {
    out.push({
      id: "delivery",
      severity: h.delivery_rate < 90 ? "error" : "warning",
      message: `Delivery rate is ${fmtPct(h.delivery_rate)} — under the 95% floor for this window.`,
      to: "deliverability",
    });
  }
  if (h.open_rate != null && (sent ?? 0) >= 50 && h.open_rate < 10) {
    out.push({
      id: "open",
      severity: "info",
      message: `Open rate is ${fmtPct(h.open_rate)} across ${fmtInt(sent)} sends — check subject lines and list quality.`,
      to: "templates",
    });
  }
  return out;
}

// ── Page ─────────────────────────────────────────────────────────────────────
const MeasureOverviewPage: React.FC = () => {
  const navigate = useNavigate();
  const [range, setRange] = useTimeRangeParam({ type: "30d" });
  const [data, setData] = useState<OverviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Monotonic request id — a superseded response can never overwrite a newer
  // one (the range toggle is one click away from a double-fetch).
  const reqSeq = useRef(0);

  useEffect(() => {
    const seq = ++reqSeq.current;
    const stale = () => reqSeq.current !== seq;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const params = new URLSearchParams({ range: toApiToken(range) });
        if (range.type === "custom") {
          const ymd = resolveRangeYmd(range);
          params.set("from", ymd.from);
          params.set("to", ymd.to);
        }
        const res: any = await apiService.get(
          `/api/admin/communications/overview?${params.toString()}`,
        );
        if (stale()) return;
        // fetchWithAuth already unwraps the route's { success, data } envelope,
        // so res.data IS the payload. Never fall back to the raw response —
        // doing so is how the old page kept "reading" a shape that never
        // existed instead of failing loudly.
        setData((res?.data ?? null) as OverviewData | null);
      } catch (e: any) {
        if (stale()) return;
        setData(null);
        setError(e?.message ?? "Failed to load communications overview");
      } finally {
        if (!stale()) setLoading(false);
      }
    })();
  }, [range]);

  const emailChannel = data?.channels?.email;
  const emailSent = emailChannel?.sent ?? null;
  const activeTriggers = data?.channels?.triggers?.active ?? null;
  const scopeLabel = describeRange(range);

  const volumeRows = useMemo(() => {
    const labels = data?.volume?.labels ?? [];
    const email = data?.volume?.email ?? [];
    return labels.map((d, i) => ({
      label: fmtDay(d),
      email: Number(email[i]) || 0,
    }));
  }, [data]);

  const anomalies = useMemo(
    () => deriveAnomalies(data, emailSent),
    [data, emailSent],
  );

  const hasData = data != null;
  const topTemplates = data?.topTemplates ?? [];
  const recent = data?.recentActivity ?? [];
  const lifetimeTemplates = (data?.topTemplatesScope ?? "lifetime") === "lifetime";

  return (
    <PillarPageShell
      pillar="measure"
      title="Communications Overview"
      subtitle="Email send volume, delivery health and the busiest templates. Push and SMS are not instrumented yet."
      helpTipId="comm.overview.title"
      rightActions={
        <TimeRangeFilter value={range} onChange={setRange} />
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <strong>Couldn&apos;t load the overview.</strong> {error} — the figures
          below are unknown, not zero.
        </Alert>
      )}

      {/* Scope is stated in plain text, not via MetricCard's `comparison` prop
          — that prop only renders inside the trend chip, so a card without a
          trend silently drops it. */}
      <Typography variant="overline" sx={{ color: "text.secondary", display: "block", mb: 1 }}>
        Email · {scopeLabel}
      </Typography>

      {/* ── KPI strip ─────────────────────────────────────────────────── */}
      {loading ? (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Grid key={i} size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
              <Skeleton variant="rounded" height={120} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
            <MetricCard
              kpi="email.total_sent"
              value={fmtInt(emailSent)}
              comparison={scopeLabel}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
            <MetricCard
              kpi="email.delivered_rate"
              value={fmtPct(data?.health?.delivery_rate)}
              comparison={scopeLabel}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
            <MetricCard
              kpi="email.open_rate"
              value={fmtPct(data?.health?.open_rate)}
              comparison="of delivered"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
            <MetricCard
              kpi="email.click_rate"
              value={fmtPct(data?.health?.click_rate)}
              comparison="of delivered"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
            <MetricCard
              kpi="email.bounce_rate.hard"
              value={fmtPct(data?.health?.bounce_rate, 2)}
              comparison={scopeLabel}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
            <Card sx={{ borderRadius: 2, height: "100%" }}>
              <CardContent>
                <Typography variant="overline" sx={{ color: "text.secondary" }}>
                  Active triggers
                </Typography>
                <Typography variant="h5" sx={{ color: "#1A5F7A", mt: 1 }}>
                  {fmtInt(activeTriggers)}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  enabled right now
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* ── Derived health checks ─────────────────────────────────────── */}
      {!loading && !error && (
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="overline"
            sx={{ color: "text.secondary", display: "block", mb: 1 }}
          >
            Health checks ({scopeLabel})
          </Typography>
          {!hasData || !data?.health ? (
            <Alert severity="info">
              No health figures were returned for this window.
            </Alert>
          ) : anomalies.length === 0 ? (
            <Alert severity="success">
              Nothing tripped a threshold: delivery{" "}
              {fmtPct(data.health.delivery_rate)}, bounce{" "}
              {fmtPct(data.health.bounce_rate, 2)}, open{" "}
              {fmtPct(data.health.open_rate)}. Checks run on the four rates this
              endpoint reports — they are not a full anomaly detector.
            </Alert>
          ) : (
            anomalies.map((a) => (
              <Alert key={a.id} severity={a.severity} sx={{ mb: 1 }}>
                {a.message}{" "}
                <Button
                  size="small"
                  onClick={() => navigate(DEEP_LINKS[a.to])}
                  sx={{ textTransform: "none", p: 0, minWidth: 0, ml: 0.5 }}
                >
                  Investigate →
                </Button>
              </Alert>
            ))
          )}
        </Box>
      )}

      {/* ── Volume + channels ─────────────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <Card sx={{ borderRadius: 2, height: "100%" }}>
            <CardContent>
              <Typography variant="overline" sx={{ color: "text.secondary" }}>
                Email volume per day ({scopeLabel})
              </Typography>
              {loading ? (
                <Skeleton variant="rounded" height={260} sx={{ mt: 1 }} />
              ) : volumeRows.length === 0 ? (
                <Box
                  sx={{
                    height: 260,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "text.secondary",
                  }}
                >
                  <Typography variant="body2">
                    {error
                      ? "Volume is unknown — the request failed."
                      : "No sends recorded in this window."}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ mt: 1 }}>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart
                      data={volumeRows}
                      margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} minTickGap={24} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} width={44} />
                      <ChartTooltip
                        formatter={(v: any) => [fmtInt(Number(v)), "Emails sent"]}
                      />
                      <Bar
                        dataKey="email"
                        name="Emails sent"
                        fill="#1A5F7A"
                        radius={[3, 3, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    Counted from LifeScribe&apos;s own send log
                    (<code>email_sending_log</code>).
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }}>
          <Card sx={{ borderRadius: 2, height: "100%" }}>
            <CardContent>
              <Typography variant="overline" sx={{ color: "text.secondary" }}>
                Channels
              </Typography>
              {loading ? (
                <Skeleton variant="rounded" height={180} sx={{ mt: 1 }} />
              ) : (
                <Box sx={{ mt: 1 }}>
                  {(
                    [
                      { key: "email", label: "📧 Email" },
                      { key: "sms", label: "💬 SMS" },
                      { key: "push", label: "🔔 Push" },
                    ] as const
                  ).map(({ key, label }) => {
                    const ch = data?.channels?.[key];
                    // reported === false ⇒ the server hardcodes 0 for this
                    // channel. Show "not reported", never a fabricated count.
                    const reported = ch?.reported !== false && ch != null;
                    return (
                      <Box
                        key={key}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          py: 1,
                          borderBottom: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <Typography variant="body2">{label}</Typography>
                        {reported ? (
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {fmtInt(ch?.sent)}
                          </Typography>
                        ) : (
                          <Chip
                            size="small"
                            variant="outlined"
                            label="not reported"
                          />
                        )}
                      </Box>
                    );
                  })}
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", display: "block", mt: 1.5 }}
                  >
                    Only email volume is instrumented today. SMS and push have no
                    feed behind this endpoint, so they are shown as unknown rather
                    than as zero.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ── Top templates ─────────────────────────────────────────────── */}
      <Card sx={{ borderRadius: 2, mb: 3 }}>
        <CardContent sx={{ pb: 1 }}>
          <Typography variant="overline" sx={{ color: "text.secondary" }}>
            Busiest templates
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
            {lifetimeTemplates
              ? "Sends and open rate are all-time counters on the template — not scoped to the selected range."
              : `Scoped to ${scopeLabel}.`}
          </Typography>
        </CardContent>
        <Divider />
        {loading ? (
          <Box sx={{ p: 2 }}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} height={40} />
            ))}
          </Box>
        ) : topTemplates.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {error
                ? "Template ranking is unknown — the request failed."
                : "No active templates with sends yet."}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ "& th": { fontWeight: 700, color: "text.secondary" } }}>
                  <TableCell>Template</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell align="right">Sends (all time)</TableCell>
                  <TableCell align="right">Open rate (all time)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {topTemplates.map((t, i) => (
                  <TableRow key={`${t.name ?? "template"}-${i}`} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t.name || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {t.category ? (
                        <Chip size="small" variant="outlined" label={t.category} />
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell align="right">{fmtInt(t.sends)}</TableCell>
                    <TableCell align="right">{fmtPct(t.open_rate)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* ── Recent activity ───────────────────────────────────────────── */}
      <Card sx={{ borderRadius: 2 }}>
        <CardContent sx={{ pb: 1 }}>
          <Typography variant="overline" sx={{ color: "text.secondary" }}>
            Latest sends
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
            The 10 most recent rows in the send log — always the latest, whatever
            range is selected above.
          </Typography>
        </CardContent>
        <Divider />
        {loading ? (
          <Box sx={{ p: 2 }}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} height={36} />
            ))}
          </Box>
        ) : recent.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {error
                ? "Recent activity is unknown — the request failed."
                : "Nothing has been sent yet."}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ "& th": { fontWeight: 700, color: "text.secondary" } }}>
                  <TableCell>Subject</TableCell>
                  <TableCell>Recipient</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">When</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recent.map((a, i) => (
                  <TableRow key={`${a.time ?? ""}-${i}`} hover>
                    <TableCell sx={{ maxWidth: 380 }}>
                      <Typography variant="body2" noWrap>
                        {a.subject || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                        {a.recipient || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {a.status ? (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={a.status}
                          color={
                            a.status === "bounced" || a.status === "failed"
                              ? "error"
                              : a.status === "opened" || a.status === "clicked"
                                ? "success"
                                : "default"
                          }
                        />
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="caption">{fmtWhen(a.time)}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>
    </PillarPageShell>
  );
};

export default MeasureOverviewPage;
